import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PATH_KEYS } from "../services/appServices";
import { useItemService } from "./useServices";
import { useState } from "react";
import { Search } from "../models/search/Search";
import {
  StoredSearch,
  CreateSearchRequest,
  FavoriteSearchesResponse,
} from "../services/searchServices";
import { parseSearchUrlParams } from "../utils/searchParamsFormatter";
import { SearchRequest } from "@/models/search/SearchRequest";
import { SearchResult } from "@/models/search/SearchResults";
import { SearchPagination } from "@/models/search/SearchPagination";
import { restError } from "@/models/client/RestResponse";

const useSearch = () => {
  const searchService = useItemService((services) => services.search);
  const queryClient = useQueryClient();

  // Track pending operations per search ID
  const [pendingFavorites, setPendingFavorites] = useState<Set<string>>(
    new Set()
  );
  const [pendingUnfavorites, setPendingUnfavorites] = useState<Set<string>>(
    new Set()
  );
  const [pendingDeletes, setPendingDeletes] = useState<Set<string>>(new Set());

  // Helper functions to check pending states for specific items
  const isFavoritePending = (searchId: string) =>
    pendingFavorites.has(searchId);
  const isUnfavoritePending = (searchId: string) =>
    pendingUnfavorites.has(searchId);
  const isDeletePending = (searchId: string) => pendingDeletes.has(searchId);
  const isAnyFavoritePending = (searchId: string) =>
    pendingFavorites.has(searchId) || pendingUnfavorites.has(searchId);

  const transformStoredSearch = (stored: StoredSearch): Search => ({
    ...parseSearchUrlParams(stored.searchUrl, stored.id),
    isFavorite: stored.isFavorite,
    favoriteLinkId: stored.favoriteLinkId,
    searchedAt: stored.createdAt,
  });

  // Queries
  const useTenantSearch = (request: SearchRequest & { enabled?: boolean }) =>
    useQuery<SearchPagination<SearchResult>>({
      queryKey: [
        PATH_KEYS.search,
        "tenant",
        request.searchTerm,
        request.entityTypes,
        request.sources,
        JSON.stringify(request.filters),
        JSON.stringify(request.sourceFilters),
        request.pageNumber,
        request.pageSize,
      ],
      queryFn: async () => {
        const response = await searchService.tenantSearch(request);
        if (response.success) return response.value;
        else throw restError(response);
      },
      enabled:
        request.enabled !== undefined
          ? request.enabled
          : !!request.searchTerm || request.entityTypes?.length === 1,
    });

  const useMyRecentSearches = () =>
    useQuery<StoredSearch[]>({
      queryKey: [PATH_KEYS.search, "recent-searches"],
      queryFn: async () => {
        const response = await searchService.getMyRecentSearches();
        if (response.success) {
          return response.value;
        } else {
          throw restError(response);
        }
      },
    });

  const useMyFavoriteSearches = () =>
    useQuery<FavoriteSearchesResponse>({
      queryKey: [PATH_KEYS.search, "favorite-searches"],
      queryFn: async () => {
        const response = await searchService.getMyFavoriteSearches();
        if (response.success) {
          return response.value;
        } else {
          throw restError(response);
        }
      },
    });

  // Mutations
  const createSearch = useMutation<Search, Error, CreateSearchRequest>({
    mutationKey: [PATH_KEYS.search, "create"],
    mutationFn: async (request) => {
      const response = await searchService.createSearch(request);
      if (response.success) return transformStoredSearch(response.value);
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.search, "recent-searches"],
      });
    },
  });

  const favoriteSearch = useMutation<Search, Error, string>({
    mutationKey: [PATH_KEYS.search, "favorite"],
    mutationFn: async (id) => {
      // Add to pending favorites
      setPendingFavorites((prev) => new Set(prev).add(id));

      try {
        const response = await searchService.favoriteSearch(id);
        if (response.success) return transformStoredSearch(response.value);
        else throw restError(response);
      } finally {
        // Remove from pending favorites
        setPendingFavorites((prev) => {
          const newSet = new Set(prev);
          newSet.delete(id);
          return newSet;
        });
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.search, "favorite-searches"],
      });
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.search, "recent-searches"],
      });
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.search, data.id],
      });
    },
  });

  const unfavoriteSearch = useMutation<Search, Error, string>({
    mutationKey: [PATH_KEYS.search, "unfavorite"],
    mutationFn: async (id) => {
      // Add to pending unfavorites
      setPendingUnfavorites((prev) => new Set(prev).add(id));

      try {
        const response = await searchService.unfavoriteSearch(id);
        if (response.success) return transformStoredSearch(response.value);
        else throw restError(response);
      } finally {
        // Remove from pending unfavorites
        setPendingUnfavorites((prev) => {
          const newSet = new Set(prev);
          newSet.delete(id);
          return newSet;
        });
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.search, "favorite-searches"],
      });
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.search, "recent-searches"],
      });
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.search, data.id],
      });
    },
  });

  const deleteSearch = useMutation<void, Error, string>({
    mutationKey: [PATH_KEYS.search, "delete"],
    mutationFn: async (id) => {
      // Add to pending deletes
      setPendingDeletes((prev) => new Set(prev).add(id));

      try {
        const response = await searchService.deleteSearch(id);
        if (!response.success) throw restError(response);
      } finally {
        // Remove from pending deletes
        setPendingDeletes((prev) => {
          const newSet = new Set(prev);
          newSet.delete(id);
          return newSet;
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.search, "favorite-searches"],
      });
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.search, "recent-searches"],
      });
    },
  });

  const deleteAllNonFavoriteSearches = useMutation<void, Error, void>({
    mutationKey: [PATH_KEYS.search, "delete-all-non-favorite"],
    mutationFn: async () => {
      const response = await searchService.deleteAllNonFavoriteSearches();
      if (!response.success) throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.search, "favorite-searches"],
      });
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.search, "recent-searches"],
      });
    },
  });

  return {
    useTenantSearch,
    useMyRecentSearches,
    useMyFavoriteSearches,
    createSearch,
    favoriteSearch,
    unfavoriteSearch,
    deleteSearch,
    deleteAllNonFavoriteSearches,
    // Helper functions to check pending states
    isFavoritePending,
    isUnfavoritePending,
    isDeletePending,
    isAnyFavoritePending,
    transformStoredSearch,
  };
};

export default useSearch;
